// Auto-crop at capture (FR-001 section 10.2): find the bill in a photo and keep only it, so the stored evidence and the model's
// input are smaller. Downscale to ~256 px, grayscale, average 6x6 blocks (specks and table grain do not count), Otsu threshold,
// bounding box of the paper (or of the ink when the background is white) plus a 3% margin. Crop only when confident: the box
// keeps at least 25% of the frame and less than 95%. The person sees the crop before upload and can keep the original.
export type CropBox = { x: number; y: number; w: number; h: number };
export type CropResult = { blob: Blob; cropped: boolean; box?: CropBox; width: number; height: number };

async function bitmapOf(file: Blob): Promise<ImageBitmap> {
  try { return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions); } catch { return createImageBitmap(file); }
}

function otsu(values: Float32Array): number {
  const hist = new Array<number>(256).fill(0);
  for (const v of values) hist[Math.max(0, Math.min(255, Math.round(v)))]++;
  const total = values.length; let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, best = 0, thr = 127;
  for (let t = 0; t < 256; t++) {
    wB += hist[t]; if (wB === 0) continue;
    const wF = total - wB; if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB, mF = (sum - sumB) / wF, between = wB * wF * (mB - mF) ** 2;
    if (between > best) { best = between; thr = t; }
  }
  return thr;
}

/** The bounding box (in block coordinates) of the blocks where pick(value) holds; null when none. */
function bbox(blocks: Float32Array, bw: number, bh: number, pick: (v: number) => boolean): CropBox | null {
  let x0 = bw, y0 = bh, x1 = -1, y1 = -1;
  for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) if (pick(blocks[y * bw + x])) { if (x < x0) x0 = x; if (y < y0) y0 = y; if (x > x1) x1 = x; if (y > y1) y1 = y; }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Where the bill is, as a fraction of the frame, or null when there is nothing confident to crop. */
export function findBill(gray: Uint8ClampedArray, sw: number, sh: number): CropBox | null {
  const B = 6; const bw = Math.ceil(sw / B), bh = Math.ceil(sh / B);
  const blocks = new Float32Array(bw * bh); const counts = new Float32Array(bw * bh);
  for (let y = 0; y < sh; y++) for (let x = 0; x < sw; x++) { const i = (Math.floor(y / B) * bw) + Math.floor(x / B); blocks[i] += gray[y * sw + x]; counts[i]++; }
  for (let i = 0; i < blocks.length; i++) blocks[i] /= counts[i] || 1;
  const t = otsu(blocks);
  const frame = bw * bh;
  const test = (box: CropBox | null) => box && box.w * box.h >= 0.25 * frame && box.w * box.h <= 0.95 * frame ? box : null;
  // paper brighter than the table: the bright region; a white background instead: the ink region
  const paper = test(bbox(blocks, bw, bh, (v) => v > t));
  const ink = paper ? null : test(bbox(blocks, bw, bh, (v) => v <= t));
  const box = paper ?? ink;
  if (!box) return null;
  const m = 0.03;
  const x = Math.max(0, box.x / bw - m), y = Math.max(0, box.y / bh - m);
  return { x, y, w: Math.min(1 - x, box.w / bw + 2 * m), h: Math.min(1 - y, box.h / bh + 2 * m) };
}

export async function autoCropReceipt(file: Blob, maxEdge = 1600, quality = 0.82): Promise<CropResult> {
  const bmp = await bitmapOf(file);
  const W = bmp.width, H = bmp.height;
  const S = 256; const scale = Math.min(1, S / Math.max(W, H));
  const sw = Math.max(1, Math.round(W * scale)), sh = Math.max(1, Math.round(H * scale));
  const small = document.createElement('canvas'); small.width = sw; small.height = sh;
  const sctx = small.getContext('2d', { willReadFrequently: true })!;
  sctx.drawImage(bmp, 0, 0, sw, sh);
  const { data } = sctx.getImageData(0, 0, sw, sh);
  const gray = new Uint8ClampedArray(sw * sh);
  for (let i = 0; i < sw * sh; i++) gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  const box = findBill(gray, sw, sh);
  const sx = box ? Math.round(box.x * W) : 0, sy = box ? Math.round(box.y * H) : 0, cw = box ? Math.round(box.w * W) : W, ch = box ? Math.round(box.h * H) : H;
  const out = Math.min(1, maxEdge / Math.max(cw, ch));
  const c = document.createElement('canvas'); c.width = Math.max(1, Math.round(cw * out)); c.height = Math.max(1, Math.round(ch * out));
  c.getContext('2d')!.drawImage(bmp, sx, sy, cw, ch, 0, 0, c.width, c.height);
  const blob: Blob = await new Promise((res) => c.toBlob((b) => res(b ?? file), 'image/jpeg', quality));
  return { blob, cropped: !!box, box: box ?? undefined, width: c.width, height: c.height };
}
